import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import type { Me } from '../../api/types';
import { IconCheckCircle, IconInfo } from '../../shared/icons';
import { AuthLayout } from './AuthLayout';

interface VerifySignupPageProps {
  token: string;
  onSignedIn: (me: Me) => void;
  onGoToSignIn: () => void;
  onGoToSignUp: () => void;
}

/**
 * The other half of sign-up: following the link from the inbox.
 *
 * The token is spent as soon as this screen loads, so there is no button to
 * press. It is a POST rather than a GET on the link itself because mail
 * clients and security scanners fetch links ahead of the reader, and a spent
 * link would leave the real person locked out.
 *
 * It ends one of two ways. A company address is signed in on the spot; a
 * personal one gets an account that waits for a person to look at it.
 */
export function VerifySignupPage({
  token,
  onSignedIn,
  onGoToSignIn,
  onGoToSignUp,
}: VerifySignupPageProps) {
  const verify = useMutation({
    mutationFn: () => api.verifySignup(token),
    onSuccess: (result) => {
      if (result.status === 'active' && result.me) {
        onSignedIn(result.me);
      }
    },
  });

  // React 18's development StrictMode mounts effects twice, and this one
  // spends a single-use token; the ref makes the second pass a no-op.
  const started = useRef(false);
  const run = verify.mutate;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
  }, [run]);

  if (verify.isError) {
    const message =
      verify.error instanceof ApiError
        ? verify.error.message
        : 'This link could not be checked.';
    return (
      <AuthLayout kicker="New account" heading="Link expired">
        <div className="auth-card">
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{message}</span>
          </div>
          <p className="field-help">
            Confirmation links work once and expire after 24 hours. Starting again sends a fresh
            one.
          </p>
          <button className="btn btn-primary btn-block" onClick={onGoToSignUp}>
            Start again
          </button>
          <button className="btn btn-block" onClick={onGoToSignIn}>
            Back to sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (verify.data?.status === 'pending_approval') {
    return (
      <AuthLayout kicker="New account" heading="Thanks, we are checking">
        <div className="auth-card">
          <div className="notice notice-ok" role="status">
            <IconCheckCircle size={14} />
            <span>Your email address is confirmed.</span>
          </div>
          <p className="field-help">
            Accounts opened with a personal email address are looked at by a person before they go
            live. We will email you as soon as yours is ready, usually within a working day. There
            is nothing else for you to do.
          </p>
          <button className="btn btn-block" onClick={onGoToSignIn}>
            Back to sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout kicker="New account" heading="Confirming your address">
      <div className="auth-card">
        <span className="cell-muted">One moment…</span>
      </div>
    </AuthLayout>
  );
}
